import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '@auth0/auth0-react';
import ExcelEditor from './ExcelEditor';
import UnifiedDocumentEditor from '../UnifiedDocumentEditor';
import SafePdfViewer from '../../shared/SafePdfViewer';
import UnsavedChangesModal from './UnsavedChangesModal';
import FileTypeSkeleton from './FileTypeSkeleton';
import { htmlToDocx, blobToBase64 } from '../../../utils/docxHandler';

/**
 * Extract body content and styles from a full HTML document
 * Returns { bodyHtml, styleSheet }
 */
function extractHtmlParts(fullHtml: string): { bodyHtml: string; styleSheet?: string } {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(fullHtml, 'text/html');

    // Extract styles from head
    const styleElement = doc.querySelector('head style');
    const styleSheet = styleElement?.textContent || '';

    // Extract body content
    const bodyElement = doc.querySelector('body');
    const bodyHtml = bodyElement?.innerHTML || fullHtml;

    return { bodyHtml, styleSheet };
  } catch (error) {
    console.error('Error parsing HTML document:', error);
    // If parsing fails, return the original HTML
    return { bodyHtml: fullHtml };
  }
}

interface SecureFileViewerProps {
  fileData: {
    FileID: string;
    FileName: string;
    FileType?: string;
    decryptedBlobUrl?: string;
    FileSize?: number;
    FolderID?: string;
  };
  isLoading: boolean;
  onSave?: (content: string, fileName: string) => Promise<void>;
  onDelete?: () => Promise<void>;
  onClose?: () => void;
  onVersionChange?: (versionId: string) => void;
  onShowVersionHistory?: () => void;
  onDownload?: () => void;
  onToggleEditMode?: () => void;
  versions?: Array<{
    id: string;
    timestamp: string;
    author?: string;
  }>;
}

const SecureFileViewer: React.FC<SecureFileViewerProps> = ({
  fileData,
  isLoading,
  onSave,
  onClose,
  versions = []
}) => {
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();
  const [editMode, setEditMode] = useState(false);
  const [editContent, setEditContent] = useState<string>('');
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const contentLoadedRef = useRef(false);

  // Excel editing state
  const [excelSheetData] = useState<any[]>([]);
  const [excelHasChanges, setExcelHasChanges] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);


  // Reset content tracking when file changes
  useEffect(() => {
    contentLoadedRef.current = false;
    setEditContent('');
    setEditMode(false);
  }, [fileData.FileID, fileData.FolderID]);

  // Cleanup on unmount and when file changes
  useEffect(() => {
    return () => {
      contentLoadedRef.current = false;
      // Scoped styles are now part of the content, so no global cleanup needed
    };
  }, [fileData.FileID]);

  // Handle unsaved changes warning
  useEffect(() => {
    if (!excelHasChanges) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [excelHasChanges]);



  // Determine file type based on MIME type and filename
  const getFileExtension = (): string => {
    const fileName = fileData.FileName.toLowerCase();
    const mimeType = (fileData.FileType || '').toLowerCase();

    // Check MIME types first (more reliable)
    if (mimeType.includes('pdf') || fileName.endsWith('.pdf')) {
      return 'pdf';
    }

    // Excel MIME types (must check before generic 'document' checks)
    if (
      mimeType.includes('spreadsheet') ||
      mimeType.includes('sheet') ||
      mimeType.includes('excel') ||
      mimeType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml') ||
      mimeType.includes('application/vnd.ms-excel') ||
      fileName.endsWith('.xlsx') ||
      fileName.endsWith('.xls') ||
      fileName.endsWith('.csv')
    ) {
      if (fileName.endsWith('.csv')) return 'text';
      return 'excel';
    }

    // Word/Document MIME types
    if (
      mimeType.includes('word') ||
      mimeType.includes('document') ||
      mimeType.includes('application/vnd.openxmlformats-officedocument.wordprocessingml') ||
      mimeType.includes('application/msword') ||
      fileName.endsWith('.docx') ||
      fileName.endsWith('.doc')
    ) {
      return 'docx';
    }

    // Text files
    if (
      mimeType.includes('text') ||
      fileName.endsWith('.txt') ||
      fileName.endsWith('.csv')
    ) {
      return 'text';
    }

    return 'unknown';
  };

  const fileType = getFileExtension();
  const canEdit = fileType === 'docx' || fileType === 'text' || fileType === 'excel';
  const isPdf = fileType === 'pdf';
  const canView = canEdit || isPdf; // Can view in unified editor

  // Load DOCX HTML from blob or stream endpoint
  const loadDocxFromStream = useCallback(async () => {
    try {
      let htmlResponse: string;

      // First, try to use the blob URL if available (already fetched by WordEditor)
      if (fileData.decryptedBlobUrl) {
        const blobResponse = await fetch(fileData.decryptedBlobUrl);
        if (!blobResponse.ok) {
          throw new Error(`Failed to fetch blob: ${blobResponse.status}`);
        }
        const htmlBlob = await blobResponse.blob();
        // Check with includes() — blob type may contain charset, e.g. "text/html; charset=utf-8"
        if (htmlBlob.type.includes('text/html') || htmlBlob.type.includes('text/plain')) {
          htmlResponse = await htmlBlob.text();
        } else {
          throw new Error(`Invalid blob type: ${htmlBlob.type}`);
        }
      } else {
        // Fallback: call stream-file endpoint directly
        const token = await getAccessTokenSilently();
        const response = await fetch('/files/stream-file', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            FileID: fileData.FileID,
            FolderID: fileData.FolderID || ''
          })
        });

        if (!response.ok) {
          throw new Error(`Failed to stream DOCX: ${response.status}`);
        }

        htmlResponse = await response.text();
      }

      if (!htmlResponse || htmlResponse.trim().length === 0) {
        throw new Error('Empty HTML content');
      }

      // Extract body HTML and styles from the full document
      const { bodyHtml, styleSheet } = extractHtmlParts(htmlResponse);

      // For iframe rendering, we need to return a complete HTML document
      // The iframe will handle all styles in isolation
      if (styleSheet && styleSheet.trim().length > 0) {
        // Reconstruct the full HTML document for iframe
        const fullDocumentHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    ${styleSheet}
  </style>
</head>
<body>
  ${bodyHtml}
</body>
</html>`;

        setEditContent(fullDocumentHtml);
      } else {
        // Fallback: wrap body HTML in minimal HTML structure
        const fullDocumentHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
</head>
<body>
  ${bodyHtml}
</body>
</html>`;

        setEditContent(fullDocumentHtml);
      }

      return true;
    } catch (error) {
      console.error('[DOCX Stream Error]:', error);
      return false;
    }
  }, [fileData.FileID, fileData.FolderID, fileData.decryptedBlobUrl, getAccessTokenSilently]);

  // Auto-trigger DOCX loading on mount/file change
  useEffect(() => {
    if (fileType === 'docx' && !editContent && !contentLoadedRef.current) {
      contentLoadedRef.current = true;
      loadDocxFromStream();
    }
  }, [fileType, fileData.FileID, editContent, loadDocxFromStream]);

  // Auto-enable view mode for viewable file types
  useEffect(() => {
    if (canView && !editMode && !isLoadingContent && editContent) {
      setEditMode(true);
    }
  }, [canView, editMode, isLoadingContent, editContent]);

  const handleEditStart = useCallback(async () => {
    // Excel files don't need pre-loading - they'll load directly from blob URL
    if (fileType === 'excel') {
      setEditMode(true);
      setIsLoadingContent(false);
      return;
    }

    // If we already have content loaded, just enter edit mode
    if (editContent) {
      setEditMode(true);
      setIsLoadingContent(false);
      return;
    }

    // Load content for other file types
    if (fileData.decryptedBlobUrl && fileType !== 'docx' && fileType !== 'excel') {
      try {
        const response = await fetch(fileData.decryptedBlobUrl);
        if (!response.ok) throw new Error('Failed to fetch file');

        if (fileType === 'text') {
          const text = await response.text();
          setEditContent(text);
        }
        setEditMode(true);
        setIsLoadingContent(false);
      } catch (error) {
        console.error('Error loading content:', error);
        setIsLoadingContent(false);
      }
    }
  }, [fileData.decryptedBlobUrl, fileType, editContent]);

  // Auto-trigger edit mode for editable file types
  useEffect(() => {
    if (isLoadingContent && !editMode) {
      handleEditStart();
    }
  }, [isLoadingContent, editMode, handleEditStart]);

  const handleSave = useCallback(async () => {
    if (!onSave || !editContent) return;

    // Stream save in background without showing loading state
    try {
      let contentToSave = editContent;

      // For DOCX files, convert HTML back to DOCX binary format
      if (fileType === 'docx') {
        try {
          const docxBlob = await htmlToDocx(editContent, fileData.FileName);
          const base64Content = await blobToBase64(docxBlob);
          contentToSave = base64Content;
        } catch (conversionError) {
          console.error('Error converting HTML to DOCX:', conversionError);
          throw new Error('Failed to convert document to DOCX format');
        }
      } else if (fileType === 'excel') {
        // For Excel files, convert CSV back to Excel binary format
        try {
          const XLSX = await import('xlsx');
          const workbook = XLSX.read(editContent, { type: 'string' });
          const excelBlob = new Blob(
            [XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })],
            { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
          );
          const base64Content = await blobToBase64(excelBlob);
          contentToSave = base64Content;
        } catch (conversionError) {
          console.error('Error converting CSV to Excel:', conversionError);
          throw new Error('Failed to convert spreadsheet to Excel format');
        }
      }

      // Stream save without blocking the editor or showing loading state
      await onSave(contentToSave, fileData.FileName);

      // For files that should always be in edit mode, don't exit edit mode
      if (isPdf) {
        setEditMode(false);
        setEditContent('');
      }
      // For other file types, keep the user in edit mode for seamless editing
    } catch (error) {
      console.error('Error saving file:', error);
    }
  }, [onSave, editContent, fileData.FileName, fileType, isPdf]);

  const handleSaveExcel = useCallback(async () => {
    if (!onSave || excelSheetData.length === 0) return;

    try {
      // Convert sheet data back to CSV/Excel format
      const XLSX = await import('xlsx');
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet(excelSheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

      const excelBlob = new Blob(
        [XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })],
        { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
      );

      const base64Content = await blobToBase64(excelBlob);
      // Stream save without showing loading state
      await onSave(base64Content, fileData.FileName);

      setExcelHasChanges(false);
    } catch (error) {
      console.error('Error saving Excel file:', error);
    }
  }, [onSave, excelSheetData, fileData.FileName]);

  const handleSaveUnsavedChanges = useCallback(async () => {
    await handleSaveExcel();
    setShowUnsavedModal(false);
    if (onClose) {
      onClose();
    }
  }, [handleSaveExcel, onClose]);

  const handleDiscardChanges = useCallback(() => {
    setExcelHasChanges(false);
    setShowUnsavedModal(false);
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  if (isLoading) {
    return <FileTypeSkeleton fileType={fileType as 'docx' | 'pdf' | 'excel' | 'text' | 'unknown'} />;
  }

  return (
    <div className="flex flex-col bg-white dark:bg-gray-900 w-full overflow-auto custom-scrollbar-main h-full">
      {/* Main content area - centralized scrollbar for all document types */}
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col h-full">
        {isLoadingContent && !editContent && !fileData.decryptedBlobUrl && fileType !== 'pdf' ? (
          // Show skeleton while loading (skip for PDF since it loads directly)
          <FileTypeSkeleton fileType={fileType as 'docx' | 'pdf' | 'excel' | 'text' | 'unknown'} />
        ) : isPdf && fileData.decryptedBlobUrl ? (
          // PDF viewer - always use SafePdfViewer for consistent, secure rendering
          <SafePdfViewer
            pdfUrl={fileData.decryptedBlobUrl}
            filename={fileData.FileName}
          />
        ) : editMode ? (
          // Edit mode - different UI for different file types (DOCX, TXT, Excel)
          <>
            {fileType === 'excel' ? (
              <ExcelEditor
                blobUrl={fileData.decryptedBlobUrl}
                onContentChange={setEditContent}
                fileName={fileData.FileName}
              />
            ) : (
              // Unified rich text editor for DOCX and TXT only
              <UnifiedDocumentEditor
                content={editContent}
                fileType={fileType as 'docx' | 'text'}
                onContentChange={setEditContent}
                onSave={handleSave}
              />
            )}
          </>
        ) : (
          // View mode - show appropriate viewer for file type
          <>
            {fileType === 'excel' && fileData.decryptedBlobUrl ? (
              // Excel viewer
              <ExcelEditor
                blobUrl={fileData.decryptedBlobUrl}
                onContentChange={setEditContent}
                fileName={fileData.FileName}
              />
            ) : !isPdf && canView && (fileData.decryptedBlobUrl || editContent || fileType === 'docx') ? (
              // Unified editor for DOCX and TXT only (not PDF)
              <UnifiedDocumentEditor
                content={editContent}
                fileType={fileType as 'docx' | 'text'}
                onContentChange={setEditContent}
                onSave={handleSave}
              />
            ) : null}

            {/* Fallback for unsupported file types */}
            {!canView && fileType === 'unknown' && (
              <div className="flex items-center justify-center w-full h-full">
                <div className="text-center">
                  <p className="text-gray-500 dark:text-gray-400 mb-4">
                    {t('File type not supported')}
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Unsaved Changes Modal */}
      <UnsavedChangesModal
        isOpen={showUnsavedModal}
        onSave={handleSaveUnsavedChanges}
        onDiscard={handleDiscardChanges}
      />
    </div>
  );
};

export default SecureFileViewer;
